// src/generators/dzial5_trygonometria/temat2_funkcje_rozszerzone/podtemat2_wzory_redukcyjne_types/ReduceCosExactTask.js
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, { parseRootFractionInput, formatLatexPreview } from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function cosValueLatex(angle) {
  const map = {
    0: "1",
    30: "\\frac{\\sqrt{3}}{2}",
    45: "\\frac{\\sqrt{2}}{2}",
    60: "\\frac{1}{2}",
    90: "0",
  };
  return map[angle];
}

function expectedNumeric(acuteAngle, alpha) {
  // cos(180 - α) = -cos α
  if (alpha === 180) return -1;
  switch (acuteAngle) {
    case 0:  return -1;
    case 30: return -Math.sqrt(3) / 2;
    case 45: return -Math.SQRT2 / 2;
    case 60: return -1 / 2;
    case 90: return 0;
    default: return NaN;
  }
}

function normalizeLatex(str) {
  return String(str)
    .replace(/\s/g, "")
    .replace(/\*/g, "")
    .replace(/sqrt\(([^)]+)\)/g, "√$1")
    .replace(/\\sqrt{([^}]+)}/g, "√$1")
    .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
    .replace(/[()]/g, "")
    .replace(/\\/g, "");
}

// ===== task =====
export function generateTask() {
  const angles = [120, 135, 150, 180];
  const alpha = angles[Math.floor(Math.random() * angles.length)];

  let acuteAngle, exactValue;
  if (alpha === 180) {
    acuteAngle = 0;
    exactValue = "-1";
  } else {
    acuteAngle = 180 - alpha;           // 60, 45, 30
    const base = cosValueLatex(acuteAngle);
    exactValue = base === "0" ? "0" : `-${base}`;
  }

  return {
    type: "ReduceCosExactTask",
    data: { alpha, acuteAngle, exactValue }
  };
}

export function renderTask({ data }) {
  const { alpha } = data;
  return (
    <div style={{ marginBottom: "1rem" }}>
      Zredukuj kąt i oblicz dokładną wartość:
      <InlineMath math={`\\ \\cos ${alpha}^\\circ`} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\cos ${task?.data?.alpha}^\\circ`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { alpha, acuteAngle, exactValue } = data;

  // meta dla dynamicznego kąta w renderUserAnswer
  if (typeof userAnswer === "object") {
    userAnswer.func = "cos";
    userAnswer.angle = alpha;
  }

  // 1) akceptuj przybliżenia
  const expectedValue = expectedNumeric(acuteAngle, alpha);
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (parsed != null && !Number.isNaN(expectedValue)) {
    if (Math.abs(parsed - expectedValue) < 0.01) return true;
  }

  // 2) fallback: porównanie latex/tekst
  if (!raw) return false;
  return normalizeLatex(raw) === normalizeLatex(exactValue);
}

export function renderAnswer({ data }) {
  const { alpha, exactValue } = data;
  return (
    <InlineMath math={`\\displaystyle \\cos ${alpha}^\\circ = ${exactValue}`} />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const angle = typeof userAnswer === "object" ? userAnswer.angle : undefined;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  // dynamiczna etykieta, bez nawiasów; bez dublowania labelu
  const dynamicLabel = angle != null ? `\\cos ${angle}^\\circ` : "\\cos\\alpha";
  return (
    <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, dynamicLabel)}`} />
  );
}

export function renderExplanation({ data }) {
  const { alpha, acuteAngle, exactValue } = data;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru redukcyjnego:
        <BlockMath math={`\\cos(180^\\circ - \\alpha) = -\\cos \\alpha`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy <InlineMath math={`\\alpha = ${alpha}^\\circ`} />:
        <BlockMath math={`\\cos ${alpha}^\\circ = -\\cos(180^\\circ - ${alpha}^\\circ)`} />
        <BlockMath math={`\\cos ${alpha}^\\circ = -\\cos ${acuteAngle}^\\circ`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Odczytujemy dokładną wartość:
        <BlockMath math={`\\cos ${acuteAngle}^\\circ = ${exactValue}`} />
      </div>
    </div>
  );
}
